import { useCallback, useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { api } from '../Api';
import { getError, otpProviderLabel } from '../utils';
import SecurityCeremony from './SecurityCeremony';
import './OtpGate.css';

/**
 * THE WITHDRAW / CONVERT / TRANSFER OTP GATES, ON THE WEBSITE.
 *
 * `backend/routes/otpGateRoutes.js` has implemented these gates for a while and
 * the mobile app speaks the contract. The website never did: WithdrawScreen
 * posted to /api/users/withdraw and ran `toast.info(data.message)` on whatever
 * came back. An OTP challenge has no `message`, so switching the gate on showed
 * the user a toast reading "undefined" and silently dropped the withdrawal.
 *
 * This closes that. One hook, one modal, used by all three money screens.
 *
 * The contract, per action:
 *   POST /api/users/{path}          -> the action, OR an { otpRequired } challenge
 *   POST /api/users/{path}/confirm  -> { ...original body, otpToken, code }
 *   POST /api/users/{path}/resend   -> { otpToken }
 *
 * When the gate is off the first POST just performs the action and nothing here
 * is ever shown, so screens can adopt this unconditionally.
 *
 * `base` moves the three calls off /api/users for an action whose gate lives in
 * its own router (Request Money approve: base '/api/money-requests').
 */
export function useOtpGate({ path, token, onSuccess, onError, base = '/api/users' }) {
  // The body of the original request, replayed verbatim on confirm.
  const pendingBody = useRef(null);
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState(false);
  // `busy` is state, so two calls inside the same tick would both read `false`
  // and fire twice. On a withdrawal that is a double spend, so the guard that
  // actually matters is this ref, which updates synchronously.
  const inFlight = useRef(false);
  const [ceremony, setCeremony] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  const startCountdown = useCallback((seconds) => {
    setCountdown(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const authHeaders = { headers: { authorization: `Bearer ${token}` } };

  const close = useCallback(() => {
    setChallenge(null);
    pendingBody.current = null;
    clearInterval(timerRef.current);
    setCountdown(0);
  }, []);

  /** Fire the action. Resolves once it either completes or a code is sent. */
  const start = useCallback(
    async (body) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      setCeremony('submit');
      try {
        const { data } = await Axios.post(`${api}${base}/${path}`, body, authHeaders);
        if (data?.otpRequired) {
          pendingBody.current = body;
          setChallenge({ ...data, code: '' });
          startCountdown(data.resendAfterSeconds || 60);
        } else {
          close();
          onSuccess?.(data);
        }
      } catch (err) {
        onError ? onError(err) : toast.error(getError(err));
      } finally {
        inFlight.current = false;
        setBusy(false);
        setCeremony(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, base, token, onSuccess, onError, close, startCountdown]
  );

  const confirm = useCallback(
    async (code) => {
      if (inFlight.current || !challenge) return;
      // Without the original body the confirm carries no password and no
      // amount, so the server verifies the code, spends it, and only then
      // refuses the action. Refuse to send at all rather than burn the code on
      // a request that cannot succeed.
      if (!pendingBody.current) {
        toast.error('This request was interrupted. Please start again.');
        close();
        return;
      }
      inFlight.current = true;
      setBusy(true);
      setCeremony('authorise');
      try {
        const { data } = await Axios.post(
          `${api}${base}/${path}/confirm`,
          { ...pendingBody.current, otpToken: challenge.otpToken, code },
          authHeaders
        );
        close();
        onSuccess?.(data);
      } catch (err) {
        // Deliberately leaves the modal open: a wrong code should cost a retry,
        // not the whole form.
        onError ? onError(err) : toast.error(getError(err));
      } finally {
        inFlight.current = false;
        setBusy(false);
        setCeremony(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [challenge, path, base, token, onSuccess, onError, close]
  );

  const resend = useCallback(async () => {
    if (inFlight.current || !challenge || countdown > 0) return;
    inFlight.current = true;
    setBusy(true);
    setCeremony('dispatch');
    try {
      const { data } = await Axios.post(
        `${api}${base}/${path}/resend`,
        { otpToken: challenge.otpToken },
        authHeaders
      );
      setChallenge((c) => ({ ...c, ...data, code: c?.code || '' }));
      startCountdown(data.resendAfterSeconds || 60);
      toast.success('A new code was sent.');
    } catch (err) {
      toast.error(getError(err) || 'Could not resend the code.');
    } finally {
      inFlight.current = false;
      setBusy(false);
      setCeremony(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge, countdown, path, base, token, startCountdown]);

  const setCode = useCallback((code) => setChallenge((c) => (c ? { ...c, code } : c)), []);

  return { challenge, busy, ceremony, countdown, start, confirm, resend, close, setCode };
}

/** The code-entry dialog plus the security overlay. Render once per screen. */
export function OtpGateModal({ gate, title = 'Confirm this request', actionLabel = 'Confirm' }) {
  const { challenge, busy, ceremony, countdown } = gate;

  return (
    <>
      <SecurityCeremony
        active={!!ceremony}
        flow={ceremony || 'submit'}
        title={ceremony === 'authorise' ? 'Authorising your request' : title}
      />

      <Modal show={!!challenge} onHide={gate.close} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title className="otp-gate__title">
            <span className="otp-gate__lock" aria-hidden="true">🔒</span> {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted otp-gate__lede">
            For your protection we sent a 6-digit code to{' '}
            <strong>{challenge?.maskedPhone}</strong>
            {challenge?.sentVia?.length
              ? ` via ${challenge.sentVia.map(otpProviderLabel).join(' & ')}`
              : ''}
            .
            {challenge?.smsFollowUpSeconds > 0 && (
              <>
                {' '}
                <small>Not received? We will also send it by SMS in about {challenge.smsFollowUpSeconds} seconds.</small>
              </>
            )}
          </p>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              gate.confirm(challenge?.code);
            }}
          >
            <Form.Group className="mb-3" controlId="otpGateCode">
              <Form.Label>{challenge?.allowPassword ? 'OTP or Password' : 'OTP Code'}</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                autoFocus
                value={challenge?.code || ''}
                onChange={(e) => gate.setCode(e.target.value)}
                placeholder={
                  challenge?.allowPassword
                    ? 'Enter the code or your password'
                    : 'Enter the 6-digit code'
                }
                required
              />
            </Form.Group>
            <Button type="submit" variant="outline-success w-100" disabled={busy}>
              {busy ? 'Verifying…' : actionLabel}
            </Button>
          </Form>
          <div className="text-center mt-3">
            {countdown > 0 ? (
              <span className="text-muted">Resend code in {countdown}s</span>
            ) : (
              <Button variant="link" onClick={gate.resend} disabled={busy}>
                Resend code
              </Button>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
